// ApplyRetentionFixModal — Phase 4 G3.7, Tier 2 (2026-05-30).
//
// The in-UI replacement for "go hand-edit phase3/data-dictionary.json"
// for the two retention finding kinds (retention_tbd /
// retention_below_floor). The resolver's suggested_fix is advisory
// prose, so this is a confirm-to-apply modal: pre-filled where a
// concrete value is derivable, the architect confirms or edits, and
// we write the table's retention_policy through the validated
// apply-retention-fix endpoint. The endpoint returns a fresh findings
// snapshot, so the panel updates in one round-trip.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2, Pencil, X } from 'lucide-react'

import { applyRetentionFix } from '@/lib/dataDictionaryFindingsApi'
import type { DataDictionaryFindingsResponse } from '@/types/dataDictionaryFindings'
import { toast } from '@/hooks/use-toast'

export interface ApplyRetentionFixModalProps {
  projectId: string
  runId: number
  tableName: string
  /** The resolver's advisory prose, shown verbatim for context. */
  suggestedFix: string | null
  /** Pre-fill for the input when a concrete value is derivable. */
  initialValue?: string
  /**
   * Where the pre-filled value came from, so we can frame how much to
   * trust it: `'profile'` = the regulatory profile's cited floor
   * (authoritative — confirm it isn't too low for you); `'provisional'`
   * = a category-derived starting guess with no citation (must be
   * confirmed against the real obligation); `null` = no pre-fill.
   */
  suggestionSource?: 'profile' | 'provisional' | null
  /** Query key to seed/refresh with the returned snapshot. */
  refetchQueryKey: unknown[]
  onClose: () => void
}

export function ApplyRetentionFixModal({
  projectId,
  runId,
  tableName,
  suggestedFix,
  initialValue,
  suggestionSource = null,
  refetchQueryKey,
  onClose,
}: ApplyRetentionFixModalProps) {
  const queryClient = useQueryClient()
  const [value, setValue] = useState(initialValue ?? '')

  const mutation = useMutation({
    mutationFn: () =>
      applyRetentionFix(projectId, runId, {
        table_name: tableName,
        retention_policy: value.trim(),
      }),
    onSuccess: (snapshot: DataDictionaryFindingsResponse) => {
      // The POST returns a fresh snapshot — seed the cache directly to
      // avoid an extra GET, then invalidate so any other observers
      // refetch too.
      queryClient.setQueryData(refetchQueryKey, snapshot)
      queryClient.invalidateQueries({ queryKey: refetchQueryKey })
      toast({
        variant: 'success',
        title: 'Retention policy updated',
        description: `Set ${tableName}.retention_policy in data-dictionary.json.`,
      })
      onClose()
    },
    onError: (err) =>
      toast({
        variant: 'destructive',
        title: 'Could not apply fix',
        description: (err as Error).message,
      }),
  })

  const trimmed = value.trim()
  const looksTbd = !trimmed || /\bTBD\b/i.test(trimmed)
  const canSubmit = !looksTbd && !mutation.isPending

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-[560px] overflow-y-auto rounded-lg border border-zinc-700 bg-zinc-900 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-zinc-100">
              <Pencil className="h-5 w-5 text-sky-400" />
              Set retention policy
            </h2>
            <p className="mt-1 text-sm text-zinc-400">
              Write a concrete retention duration for{' '}
              <span className="font-mono text-zinc-200">{tableName}</span>.
              Cite the governing standard so the value is audit-defensible.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {suggestedFix && (
          <div className="mb-4 rounded border border-emerald-900/40 bg-emerald-950/20 p-2.5 text-xs text-emerald-200/85">
            <strong className="text-emerald-300">Resolver suggestion: </strong>
            {suggestedFix}
          </div>
        )}

        {/* Honesty banner about the pre-filled value: a profile floor
            is authoritative (but may be a minimum); a provisional guess
            is uncited and MUST be confirmed. Either way the architect
            ratifies — we never auto-write retention. */}
        {suggestionSource === 'profile' && (
          <div className="mb-4 rounded border border-sky-900/50 bg-sky-950/30 p-2.5 text-xs text-sky-200/90">
            Pre-filled with the regulatory profile's{' '}
            <strong>cited minimum</strong>. Confirm it isn't lower than
            your actual obligation, then keep or raise it.
          </div>
        )}
        {suggestionSource === 'provisional' && (
          <div className="mb-4 rounded border border-amber-900/50 bg-amber-950/30 p-2.5 text-xs text-amber-200/90">
            Pre-filled a <strong>provisional starting value</strong> from
            this table's category — it carries <em>no</em> citation and
            is a guess, not authoritative. Confirm it against your real
            retention obligation and cite the governing standard before
            you apply.
          </div>
        )}

        <label className="mb-1 block text-sm font-medium text-zinc-300">
          Retention policy
        </label>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          className="mb-1 w-full rounded border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-sky-600 focus:outline-none"
          placeholder='e.g. "12 months per PCI DSS Req 10.5.1"'
        />
        {looksTbd && trimmed.length > 0 && (
          <p className="mb-3 text-[11px] text-amber-300">
            Still looks like a placeholder — enter a concrete duration.
          </p>
        )}
        {!looksTbd && (
          <p className="mb-3 text-[11px] text-zinc-500">
            This overwrites the table's <code>retention_policy</code> in
            place.
          </p>
        )}

        {mutation.isError && (
          <div className="mb-3 flex items-start gap-2 rounded border border-red-800 bg-red-950/40 p-2 text-sm text-red-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{(mutation.error as Error).message}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded border border-zinc-700 px-3 py-1.5 text-sm text-zinc-200 hover:bg-zinc-800"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit}
            className="flex items-center gap-2 rounded bg-sky-600 px-3 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 hover:bg-sky-500"
          >
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Apply fix
          </button>
        </div>
      </div>
    </div>
  )
}
